import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, ArrowRight, Timer } from "lucide-react";
import { useLang } from "./LanguageContext";
import { TRX_URL } from "./content";

const pad = (n) => String(n).padStart(2, "0");

// Rolling countdown to the next local midnight so the flash offer always feels live.
const useCountdown = () => {
  const calc = () => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(24, 0, 0, 0);
    const diff = Math.max(0, end - now);
    return {
      h: Math.floor(diff / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return t;
};

export const PromoBanner = ({ open, onClose }) => {
  const { t } = useLang();
  const { h, m, s } = useCountdown();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: -50 }}
          animate={{ y: 0 }}
          exit={{ y: -50, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          data-testid="promo-banner"
          className="fixed top-0 inset-x-0 z-[60] h-11 bg-[#ccff00] text-black flex items-center"
        >
          <a
            href={TRX_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="promo-cta"
            className="group h-full max-w-7xl mx-auto w-full px-10 md:px-12 flex items-center justify-center gap-3"
          >
            <Sparkles className="hidden sm:block h-4 w-4 shrink-0" />
            <p className="text-xs sm:text-sm font-semibold tracking-tight truncate">
              {t.promo.text}
            </p>
            <span
              data-testid="promo-countdown"
              className="hidden sm:inline-flex items-center gap-1.5 shrink-0 bg-black text-[#ccff00] px-2.5 py-1 font-display font-bold text-xs tabular-nums"
            >
              <Timer className="h-3.5 w-3.5" />
              <span className="hidden md:inline text-[10px] font-sans font-semibold text-[#ccff00]/70 uppercase tracking-wider mr-2">
                {t.promo.ends}
              </span>
              {pad(h)}:{pad(m)}:{pad(s)}
            </span>
            <span className="hidden lg:inline-flex items-center gap-1 text-xs font-bold underline underline-offset-2 whitespace-nowrap">
              {t.promo.cta}
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>
          <button
            data-testid="promo-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center hover:bg-black/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
